// SPLIT FROM `useRetainSupportedStaysParams.ts` FOR A BUNDLE REASON — do not merge them back.
//
// `useRetainSupportedStaysParams` shares a splitChunks `default`-group candidate with the UTM helpers,
// `hotelRoutes` and one icon. That cluster is compared against the group's inherited `minSize` of
// 20,000 bytes (see config/webpack/splitChunksBudget.ts): under it the modules stay inlined in their
// consumers, over it rspack extracts them as a separate chunk — which 35 route groups reference, so
// each one pays an extra async request and `content/registryadmin` breaches the parity gate's ceiling.
//
// With both hooks in one file the cluster measured 20,755 (755 OVER the floor, gate red at 40 requests).
// This hook is the larger half and its consumers are a strict subset, so moving it out drops the
// cluster to 18,730 and the gate back to 38. Headroom is only ~1,270 bytes: adding a few hundred bytes
// to this hook's former neighbours re-crosses the line, and the symptom appears in registryadmin rather
// than anywhere near the code that caused it.
import qs from 'query-string';
import { useCallback } from 'react';
import { useQueryParamHelper } from '@shared/core/queryString';

import { TRANSFERRED_STAYS_QUERY_PARAMS } from './useRetainSupportedStaysParams';

interface UseRetainSupportedParamsInRedirectUrl {
  retainSupportedParamsInStaysRedirectUrl: (targetUrl: string) => string;
}

/**
 * Hook for enhancing redirect URLs by retaining supported query parameters from the current Stays page.
 *
 * Retains the same params as `useRetainSupportedStaysParams` — `discountCode`, `weddingCode` and both
 * Share Your Stay codes — but for the event-redirect service's nested URL shape: if the destination URL (inside
 * the `destination` param) has `idType=stays`, they are injected into it rather than into the outer
 * URL, which is the only place the eventual Stays page will read them from.
 *
 * @returns {UseRetainSupportedParamsInRedirectUrl} Object containing a function to enhance redirect URLs.
 */
export const useRetainSupportedParamsInStaysRedirectUrl = (): UseRetainSupportedParamsInRedirectUrl => {
  const queryParamHelper = useQueryParamHelper();
  const discountCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.discountCode);
  const joyReferralRewardCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.joyReferralRewardCode);
  const joyReferralCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.joyReferralCode);
  const eventGroupCode = queryParamHelper.getValueString(TRANSFERRED_STAYS_QUERY_PARAMS.weddingCode);

  const retainSupportedParamsInStaysRedirectUrl = useCallback(
    (redirectUrl: string): string => {
      // Every retained param is optional and independent, so the bail-out has to be "none of them",
      // not "no discount code". A referred guest arrives with a referral code and no discount code —
      // gating on the discount code alone dropped both of theirs on this path while still claiming to
      // retain them.
      if (!redirectUrl || (!discountCode && !joyReferralRewardCode && !joyReferralCode && !eventGroupCode)) return redirectUrl;

      // Parse outer URL (e.g. https://example.com/?destination=...)
      const { url: baseUrl, query: outerQuery, fragmentIdentifier: outerFragment } = qs.parseUrl(redirectUrl, { parseFragmentIdentifier: true });

      const destinationEncoded = outerQuery.destination as string | undefined;
      if (!destinationEncoded) return redirectUrl;

      // Decode and parse the destination URL (e.g. /search?...&idType=stays)
      const destinationDecoded = decodeURIComponent(destinationEncoded);
      const { url: innerUrl, query: innerQuery, fragmentIdentifier: innerFragment } = qs.parseUrl(destinationDecoded, { parseFragmentIdentifier: true });

      // Only modify the destination if it's a Stays URL
      if (innerQuery.idType !== 'stays') return redirectUrl;

      // Inject supported params into the decoded destination URL. Each is spread in only when the
      // current URL actually carries it: writing the key unconditionally would hand `stringifyUrl` an
      // empty value, which it drops — deleting a code the destination itself already carried.
      const retainedParams = {
        ...(discountCode && { discountCode }),
        ...(joyReferralRewardCode && { joyReferralRewardCode }),
        ...(joyReferralCode && { joyReferralCode }),
        ...(eventGroupCode && { weddingCode: eventGroupCode }),
      };

      const updatedDestination = qs.stringifyUrl(
        {
          url: innerUrl,
          query: {
            ...innerQuery,
            ...retainedParams,
          },
          fragmentIdentifier: innerFragment,
        },
        {
          skipEmptyString: true,
          skipNull: true,
          arrayFormat: 'comma',
        },
      );

      // Re-encode the destination and return the updated outer URL
      return qs.stringifyUrl(
        {
          url: baseUrl,
          query: { ...outerQuery, destination: updatedDestination },
          fragmentIdentifier: outerFragment,
        },
        {
          skipEmptyString: true,
          skipNull: true,
        },
      );
    },
    [discountCode, joyReferralRewardCode, joyReferralCode, eventGroupCode],
  );

  return { retainSupportedParamsInStaysRedirectUrl };
};
